import { useCallback, useEffect, useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ProtectedRoute } from '../components/auth/index.js';
import { GitHubActivityPanel } from '../components/features/github-activity/index.js';
import { useAuth } from '../hooks/index.js';
import {
  useMemberGithubActivities,
  useMemberGithubAggregateActivities,
  useMemberGithubAggregateByRepo,
  useMemberGithubAggregatePartners,
  useMemberGithubAggregateSummary,
  useMemberGithubAggregateTimeseries,
  useMemberGithubByRepo,
  useMemberGithubPartners,
  useMemberGithubSummary,
  useMemberGithubTimeseries,
  useMemberList,
} from '../hooks/useMember.js';
import { buildNicknameByEmail } from '../lib/displayName.js';
import type { Member, MemberGithubActivityParams } from '../types/api.js';
import {
  buildGithubActivityPeriodParams,
  buildGithubActivityPreviousPeriodParams,
  parseGithubActivitySearch,
  resolveGithubActivitySearch,
  stripGithubActivitySearchDefaults,
  type GithubActivityMember,
  type GithubActivitySearch,
} from './github-activity-search.js';
import { GitHubActivityScopeBar } from './github-activity-scope-bar.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business GitHub Activity ページ。全体 / チーム / 個人 のスコープで GitHub 活動を集計表示する。メンバー候補とチーム一覧は useMemberList (engineers + qa のうち GitHub login を持つ者) から動的に生成する。ページ状態は URL search params に同期する
 * @graph-connects useAuth [calls] ログインユーザー取得
 * @graph-connects useMemberList [calls] チーム一覧・メンバー選択肢を解決
 * @graph-connects GitHubActivityPanel [calls] dashboard 描画
 */
export const Route = createFileRoute('/github-activity')({
  component: GitHubActivityRoute,
  validateSearch: parseGithubActivitySearch,
});

/**
 * ルートガード。PI Div 所属者・admin 全員がアクセス可能（サイドバー表示と同一の SSOT で判定）。
 * VIEW_MEMBERS 権限の有無は問わない。横断 PMO・外部社員は弾く。
 * @graph-connects ProtectedRoute [calls] ページアクセス可否を判定しガードする
 */
function GitHubActivityRoute() {
  return (
    <ProtectedRoute pageKey="githubActivity">
      <GitHubActivityPage />
    </ProtectedRoute>
  );
}

/**
 * メンバー一覧から GitHub login を持つ者だけを集計対象として拾う。
 * login が無いメンバーは activity API を引けないため候補から外す。
 * @graph-connects none
 */
function pickActivityMembers(members: Member[]): GithubActivityMember[] {
  return members
    .filter((member) => member.githubName !== null && member.githubName.length > 0)
    .map((member) => ({
      email: member.email,
      githubLogin: member.githubName ?? '',
      displayName: member.name,
      teamName: member.teamName,
    }));
}

/**
 * 当該期間の MemberGithubActivityParams を作る。
 * @graph-business dashboard の通常集計と AI レビューカード向け bot-only 集計で使う期間・bot 条件を作る
 * @graph-connects buildGithubActivityPeriodParams [calls] 期間の from/to を計算
 */
function buildActivityParams(
  periodDays: number,
  includeBotsOnly = false,
): MemberGithubActivityParams {
  const { from, to } = buildGithubActivityPeriodParams(periodDays);
  return {
    range: 'custom',
    from,
    to,
    ...(includeBotsOnly ? { includeBotsOnly: true, excludeBots: false } : {}),
  };
}

/**
 * 1 つ前の同一期間の MemberGithubActivityParams を作る。前期比計算用。
 * @graph-business dashboard の前期比較 API に渡す直前期間の範囲条件を作る
 * @graph-connects buildGithubActivityPreviousPeriodParams [calls] 前期間の from/to を計算
 */
function buildPreviousPeriodParams(periodDays: number): MemberGithubActivityParams {
  const { from, to } = buildGithubActivityPreviousPeriodParams(periodDays);
  return { range: 'custom', from, to };
}

/** @graph-connects none */
function GitHubActivityPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate({ from: '/github-activity' });
  const rawSearch = Route.useSearch();
  const search = useMemo(() => resolveGithubActivitySearch(rawSearch), [rawSearch]);

  const { data: memberListData } = useMemberList();

  const allMembers = useMemo(
    () => [...(memberListData?.engineers ?? []), ...(memberListData?.qa ?? [])],
    [memberListData],
  );

  const nicknameByEmail = useMemo(() => buildNicknameByEmail(allMembers), [allMembers]);

  const activityMembers = useMemo(() => pickActivityMembers(allMembers), [allMembers]);

  const availableTeams = useMemo(() => {
    const names = new Set<string>();
    for (const member of activityMembers) {
      if (member.teamName && member.teamName.length > 0) {
        names.add(member.teamName);
      }
    }
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [activityMembers]);

  /**
   * 部分更新の URL push。current から派生した sparse search を navigate に渡す。
   * @graph-connects none
   */
  const updateSearch = useCallback(
    (patch: Partial<GithubActivitySearch>) => {
      // sparse 表現で URL に push する。validateSearch (parseSearch) が default を埋めて完全形に戻す
      void navigate({
        search: (prev) => stripGithubActivitySearchDefaults({ ...prev, ...patch }),
        replace: false,
      });
    },
    [navigate],
  );

  // ログインユーザー or 単独メンバーを初期選択する (URL 未指定時のみ)
  // updateSearch は navigate ラッパ。deps から外しても参照同一性で過剰再 effect は発生しない
  useEffect(() => {
    if (search.email) return;
    const fallback = user?.email ?? activityMembers[0]?.email;
    if (!fallback) return;
    updateSearch({ email: fallback });
  }, [search.email, user?.email, activityMembers, updateSearch]);

  // チーム選択肢が確定したら初期チームを選ぶ (URL 未指定時のみ)
  useEffect(() => {
    if (search.team) return;
    if (availableTeams.length > 0) {
      updateSearch({ team: availableTeams[0] });
    }
  }, [search.team, availableTeams, updateSearch]);

  const periodOptions = [
    { value: '7', label: t('time.last7Days') },
    { value: '14', label: t('time.last14Days') },
    { value: '30', label: t('time.last30Days') },
    { value: '90', label: t('time.last90Days') },
    { value: '180', label: t('time.last180Days') },
    { value: '365', label: t('time.last1Year') },
  ];

  // 個人スコープが指すメンバー
  const dashboardMember = useMemo(() => {
    if (!search.email) return null;
    return activityMembers.find((member) => member.email === search.email) ?? null;
  }, [search.email, activityMembers]);

  const personalGithubLogin = dashboardMember?.githubLogin ?? null;
  const periodDays = Number(search.days);

  const githubActivityParams = useMemo(() => buildActivityParams(periodDays), [periodDays]);
  const previousActivityParams = useMemo(() => buildPreviousPeriodParams(periodDays), [periodDays]);
  const botOnlyParams = useMemo(() => buildActivityParams(periodDays, true), [periodDays]);

  // スコープ別の集計対象 logins。aggregate hook は logins.length === 0 で fetch を skip する。
  const aggregateLogins = useMemo(() => {
    if (search.scope === 'all') {
      return activityMembers.map((member) => member.githubLogin);
    }
    if (search.scope === 'team' && search.team) {
      return activityMembers
        .filter((member) => member.teamName === search.team)
        .map((member) => member.githubLogin);
    }
    return [];
  }, [search.scope, search.team, activityMembers]);

  const individualGithubLogin = search.scope === 'member' ? personalGithubLogin : null;

  const individualSummary = useMemberGithubSummary(individualGithubLogin, githubActivityParams);
  const individualPrevSummary = useMemberGithubSummary(
    individualGithubLogin,
    previousActivityParams,
  );
  const individualTimeseries = useMemberGithubTimeseries(
    individualGithubLogin,
    githubActivityParams,
  );
  const individualByRepo = useMemberGithubByRepo(individualGithubLogin, githubActivityParams);
  const individualPartners = useMemberGithubPartners(individualGithubLogin, githubActivityParams);
  const individualBotPartners = useMemberGithubPartners(individualGithubLogin, botOnlyParams);
  const individualActivities = useMemberGithubActivities(
    individualGithubLogin,
    githubActivityParams,
  );

  const aggregateSummary = useMemberGithubAggregateSummary(aggregateLogins, githubActivityParams);
  const aggregatePrevSummary = useMemberGithubAggregateSummary(
    aggregateLogins,
    previousActivityParams,
  );
  const aggregateTimeseries = useMemberGithubAggregateTimeseries(
    aggregateLogins,
    githubActivityParams,
  );
  const aggregateByRepo = useMemberGithubAggregateByRepo(aggregateLogins, githubActivityParams);
  const aggregatePartners = useMemberGithubAggregatePartners(aggregateLogins, githubActivityParams);
  const aggregateBotPartners = useMemberGithubAggregatePartners(aggregateLogins, botOnlyParams);
  const aggregateActivities = useMemberGithubAggregateActivities(
    aggregateLogins,
    githubActivityParams,
  );

  // N=1 (集計対象 1 名) でスコープラジオを抑制する。メンバーが増えると自動復活する
  const isSingleMemberMode = activityMembers.length <= 1;
  const isAggregateScope = search.scope === 'all' || search.scope === 'team';

  const handleDashboardEmailChange = (email: string | undefined) => {
    updateSearch({ email });
  };

  const dashboardEmptyMessage: string | null = (() => {
    if (isAggregateScope && aggregateLogins.length === 0) {
      if (search.scope === 'team' && availableTeams.length === 0) {
        return t('githubActivity.scope.noTeamsAvailable');
      }
      return t('githubActivity.scope.noLoginsAvailable');
    }
    if (search.scope === 'member' && !search.email) {
      return t('githubActivity.scope.noMemberSelected');
    }
    if (search.scope === 'member' && search.email && !personalGithubLogin) {
      return t('githubActivity.scope.memberNoLogin');
    }
    return null;
  })();

  const dashboardScopeLabel = (() => {
    if (search.scope === 'all') {
      return `${t('githubActivity.scope.all')} (${String(aggregateLogins.length)})`;
    }
    if (search.scope === 'team') {
      return `${search.team ?? '-'} (${String(aggregateLogins.length)})`;
    }
    if (personalGithubLogin) {
      return personalGithubLogin;
    }
    return t('githubActivity.scope.member');
  })();

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="animate-content-reveal">
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('githubActivity.title')}
        </h1>
        <p className="mt-1 text-[var(--color-text-secondary)]">{t('githubActivity.description')}</p>
      </div>

      <GitHubActivityScopeBar
        isSingleMemberMode={isSingleMemberMode}
        scope={search.scope}
        selectedTeam={search.team}
        selectedEmail={search.email}
        days={search.days}
        members={activityMembers}
        nicknameByEmail={nicknameByEmail}
        availableTeams={availableTeams}
        dashboardScopeLabel={dashboardScopeLabel}
        periodOptions={periodOptions}
        onScopeChange={(scope) => {
          updateSearch({ scope });
        }}
        onTeamChange={(team) => {
          updateSearch({ team });
        }}
        onEmailChange={handleDashboardEmailChange}
        onDaysChange={(days) => {
          updateSearch({ days });
        }}
      />

      <section
        aria-labelledby="github-activity-realtime-heading"
        className="space-y-3 animate-content-reveal"
      >
        <header className="flex items-center gap-3">
          <h2
            id="github-activity-realtime-heading"
            className="text-sm font-semibold uppercase tracking-wider text-[var(--color-text-muted)]"
          >
            {t('githubActivity.sections.realtime')}
          </h2>
          <span className="h-px flex-1 bg-[var(--color-border)]" />
        </header>
        {isAggregateScope ? (
          <GitHubActivityPanel
            scopeLabel={dashboardScopeLabel}
            summary={aggregateSummary.data}
            prevSummary={aggregatePrevSummary.data}
            isSummaryLoading={aggregateSummary.isLoading}
            isSummaryError={aggregateSummary.isError}
            timeseries={aggregateTimeseries.data}
            byRepo={aggregateByRepo.data}
            partners={aggregatePartners.data}
            botPartners={aggregateBotPartners.data}
            activities={aggregateActivities.data}
            isActivitiesLoading={aggregateActivities.isLoading}
            isActivitiesError={aggregateActivities.isError}
            periodDays={periodDays}
            emptyMessage={dashboardEmptyMessage}
          />
        ) : (
          <GitHubActivityPanel
            scopeLabel={dashboardScopeLabel}
            summary={individualSummary.data}
            prevSummary={individualPrevSummary.data}
            isSummaryLoading={individualSummary.isLoading}
            isSummaryError={individualSummary.isError}
            timeseries={individualTimeseries.data}
            byRepo={individualByRepo.data}
            partners={individualPartners.data}
            botPartners={individualBotPartners.data}
            activities={individualActivities.data}
            isActivitiesLoading={individualActivities.isLoading}
            isActivitiesError={individualActivities.isError}
            periodDays={periodDays}
            emptyMessage={dashboardEmptyMessage}
          />
        )}
      </section>
    </div>
  );
}
